/**
 * GRANSKARE FÖR KONTO- OCH FIRMASVAREN.
 *
 * De här fyra rutterna är de enda stommen själv anropar (inloggningen och
 * firmaväljaren), så deras granskare står färdiga. De övriga skrivs av den som
 * bygger skärmen — mönstret är detsamma, och hjälparna står i api/granska.ts.
 *
 * TVÅ SAKER ATT HÄRMA
 *   Returtypen är KONTRAKTETS typ. `Granskare<MeResponse>` betyder att tsc
 *   faller här om MeResponse får ett fält till. Skriv aldrig `Granskare<any>`
 *   och aldrig en egen spegeltyp.
 *
 *   Fältnamnet i andra argumentet är sökvägen i svaret ('companies[2].role').
 *   Det hamnar i felets orsak och är skillnaden mellan "svaret hade fel form"
 *   och "svaret hade fel form i companies[2].role".
 */

import { ALL_ROLES } from '@delat/routes';
import type {
  AccountProof,
  AccountRow,
  AuthRequestCodeResponse,
  AuthVerifyResponse,
  CompanySummary,
  CreateCompanyResponse,
  DeleteCompanyResponse,
  LicenseProof,
  MeResponse,
  RemoveMemberResponse,
  SubscriptionStatus,
} from '@delat/types';
import {
  accountId,
  companyId,
  enAv,
  flagga,
  lista,
  millisekunder,
  objekt,
  orgnummer,
  text,
} from '../granska';
import type { Granskare } from '../granska';

/**
 * SubscriptionStatus finns bara som TYP i shared/types.ts — till skillnad från
 * ALL_ROLES och PAID_ROLES, som är data. Listan måste därför skrivas här, och
 * raden under bevisar att den är UTTÖMMANDE: läggs ett läge till i kontraktet
 * blir `Uttommande` never och tsc faller på den här filen i stället för att
 * granskaren tyst börjar avvisa det nya läget. Samma grepp som
 * PriceMissingRoleIsPriceRole i shared/types.ts.
 * Se LUCKOR i överlämningen: konstanten hör egentligen hemma i shared/.
 */
export const PRENUMERATIONSLAGEN = [
  'trialing',
  'active',
  'past_due',
  'locked',
] as const satisfies readonly SubscriptionStatus[];

export type UttommandePrenumerationslagen = [SubscriptionStatus] extends [
  (typeof PRENUMERATIONSLAGEN)[number],
]
  ? true
  : never;

export const granskaKontobevis: Granskare<AccountProof> = (v) => {
  const o = objekt(v, 'account_proof');
  return {
    token: text(o['token'], 'account_proof.token'),
    expires_at: millisekunder(o['expires_at'], 'account_proof.expires_at'),
  };
};

export const granskaLicensbevis: Granskare<LicenseProof> = (v) => {
  const o = objekt(v, 'license');
  return {
    company_id: companyId(o['company_id'], 'license.company_id'),
    token: text(o['token'], 'license.token'),
    expires_at: millisekunder(o['expires_at'], 'license.expires_at'),
  };
};

export const granskaKonto: Granskare<AccountRow> = (v) => {
  const o = objekt(v, 'account');
  return {
    id: accountId(o['id'], 'account.id'),
    email: text(o['email'], 'account.email'),
    name: text(o['name'], 'account.name'),
    created_at: millisekunder(o['created_at'], 'account.created_at'),
    updated_at: millisekunder(o['updated_at'], 'account.updated_at'),
  };
};

/**
 * CompanySummary. `role` är den roll servern LÄST UR D1 — det är den och inte
 * licensbevisets kopia som avgör vilket läge användaren får se (beslut 11 mot
 * 12, och worker/src/auth/authenticate.ts).
 */
export const granskaFirma: Granskare<CompanySummary> = (v) => {
  const o = objekt(v, 'company');
  return {
    id: companyId(o['id'], 'company.id'),
    name: text(o['name'], 'company.name'),
    org_number: orgnummer(o['org_number'], 'company.org_number'),
    role: enAv(o['role'], 'company.role', ALL_ROLES),
    is_owner: flagga(o['is_owner'], 'company.is_owner'),
    subscription_status: enAv(
      o['subscription_status'],
      'company.subscription_status',
      PRENUMERATIONSLAGEN,
    ),
  };
};

export const granskaMe: Granskare<MeResponse> = (v) => {
  const o = objekt(v, 'me');
  return {
    account: granskaKonto(o['account']),
    account_proof: granskaKontobevis(o['account_proof']),
    companies: lista(o['companies'], 'companies', granskaFirma),
    licenses: lista(o['licenses'], 'licenses', granskaLicensbevis),
  };
};

/** AUTH_VERIFY svarar med exakt samma kropp som ME (AuthVerifyResponse = MeResponse). */
export const granskaAuthVerify: Granskare<AuthVerifyResponse> = granskaMe;

/**
 * AUTH_REQUEST_CODE. Svaret är { sent: true } och ingenting mer — det säger
 * med FLIT inte om kontot fanns, och det bär aldrig koden. Skulle det göra det
 * vore rutten en orakelfråga som läcker vilka e-postadresser som är kunder.
 */
export const granskaKodSkickad: Granskare<AuthRequestCodeResponse> = (v) => {
  const o = objekt(v, 'auth_request_code');
  const skickat = flagga(o['sent'], 'sent');
  if (!skickat) throw new Error('sent var false, vilket kontraktet inte tillåter');
  return { sent: true };
};

export const granskaSkapadFirma: Granskare<CreateCompanyResponse> = (v) => {
  const o = objekt(v, 'create_company');
  return {
    company: granskaFirma(o['company']),
    license: granskaLicensbevis(o['license']),
  };
};

export const granskaBorttagenMedlem: Granskare<RemoveMemberResponse> = (v) => {
  const o = objekt(v, 'remove_member');
  const borttagen = flagga(o['removed'], 'removed');
  if (!borttagen) throw new Error('removed var false, vilket kontraktet inte tillåter');
  return { removed: true };
};

/**
 * DELETE_COMPANY. Samma form som RemoveMemberResponse och av samma skäl: det
 * finns ingen rad kvar att beskriva. Ett `deleted: false` finns inte i
 * kontraktet — servern svarar med ett fel i stället — så det avvisas här.
 */
export const granskaRaderadFirma: Granskare<DeleteCompanyResponse> = (v) => {
  const o = objekt(v, 'delete_company');
  const raderad = flagga(o['deleted'], 'deleted');
  if (!raderad) throw new Error('deleted var false, vilket kontraktet inte tillåter');
  return { deleted: true };
};
